import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { themes } from './themes';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [themeName, setThemeName] = useState(() => {
    return localStorage.getItem('app-theme') || 'dark';
  });

  const theme = useMemo(() => themes[themeName] || themes.dark, [themeName]);

  useEffect(() => {
    localStorage.setItem('app-theme', themeName);

    const root = document.documentElement;

    root.style.setProperty('--app-bg', theme.colors.appBg);
    root.style.setProperty('--surface', theme.colors.surface);
    root.style.setProperty('--surface-strong', theme.colors.surfaceStrong);
    root.style.setProperty('--border', theme.colors.border);
    root.style.setProperty('--border-soft', theme.colors.borderSoft);

    root.style.setProperty('--text-primary', theme.colors.textPrimary);
    root.style.setProperty('--text-secondary', theme.colors.textSecondary);
    root.style.setProperty('--text-muted', theme.colors.textMuted);

    root.style.setProperty('--primary', theme.colors.primary);
    root.style.setProperty('--primary-strong', theme.colors.primaryStrong);
    root.style.setProperty('--primary-gradient', theme.colors.primaryGradient);
    // --accent is an alias of --primary used across buttons and active states
    root.style.setProperty('--accent', theme.colors.primary);

    root.style.setProperty('--header-bg', theme.colors.headerBg);
    root.style.setProperty('--header-text', theme.colors.headerText);

    root.style.setProperty('--success', theme.colors.success);
    root.style.setProperty('--danger', theme.colors.danger);

    root.style.setProperty('--input-bg', theme.colors.inputBg);
    root.style.setProperty('--input-border', theme.colors.inputBorder);

    root.style.setProperty('--table-border', theme.colors.tableBorder);
    root.style.setProperty('--table-row-border', theme.colors.tableRowBorder);

    root.style.setProperty('--error-bg', theme.colors.errorBg);
    root.style.setProperty('--error-text', theme.colors.errorText);

    root.style.setProperty('--popup-bg', theme.colors.popupBg);
    root.style.setProperty('--popup-text', theme.colors.popupText);
    root.style.setProperty('--popup-border', theme.colors.popupBorder);
    root.style.setProperty('--popup-item-bg', theme.colors.popupItemBg);
    root.style.setProperty('--popup-item-border', theme.colors.popupItemBorder);

    root.style.setProperty('--blur', theme.effects.blur);
    root.style.setProperty('--shadow', theme.effects.shadow);
    root.style.setProperty('--radius-lg', theme.effects.radiusLg);
    root.style.setProperty('--radius-md', theme.effects.radiusMd);
    root.style.setProperty('--radius-sm', theme.effects.radiusSm);
  }, [theme, themeName]);

  const value = useMemo(
    () => ({
      themeName,
      setThemeName,
      toggleTheme: () => setThemeName((prev) => (prev === 'dark' ? 'light' : 'dark')),
    }),
    [themeName]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used inside ThemeProvider');
  }

  return context;
}